import { useEffect, useRef } from 'react';

import { cn } from '@/lib/cn';

interface Props {
  /** H.264 RGB video URL (color channels). */
  rgbUrl: string;
  /** H.264 grayscale video URL (alpha mask — white=opaque, black=transparent). */
  alphaUrl: string;
  /** Poster for both videos while buffering. */
  poster?: string;
  /**
   * Known backdrop color the RGB stream was composited over (e.g. '#FF00FF'
   * for clips that went through the chroma-key magenta preprocessing).
   * When set, the per-pixel decomposite formula
   *   subject = (composite − bg × (1 − α)) / α
   * is applied on semi-transparent edge pixels so the border doesn't
   * carry the backdrop tint. Omit (or undefined) when the source backdrop
   * is unknown or arbitrary — then we fall back to per-pixel despill.
   */
  knownBgHex?: string;
  className?: string;
  /**
   * I-581 (emote al tavolo): chiamata UNA volta, quando TUTTI E DUE i flussi hanno il primo frame (`loadeddata`). Chi la passa
   * decide quanto aspettare e che cosa mostrare nel frattempo. Opzionale: gli altri usi non cambiano.
   */
  onPronto?: () => void;
  /** I-581: uno dei due flussi non si carica (URL che non risponde, formato illeggibile). Opzionale. */
  onErrore?: () => void;
  /**
   * I-581: allo smontaggio i due lettori si LIBERANO (`pause`, niente `src`, `load()`), invece di restare in memoria fino alla
   * raccolta: sul telefono i lettori video sono pochi, e una bolla di 2,5 s non deve tenerne due occupati. Opzionale.
   */
  liberaAllUscita?: boolean;
  /**
   * Il banner come la bolla: `true` = i due flussi in `pause()`, il ciclo di composizione fermo (`cancelAnimationFrame`) e l'ultimo
   * fotogramma che resta sulla tela; da `true` a `false` riparte (`play()` col `catch`). Per scheda nascosta o figura fuori vista.
   * Opzionale: assente = `false` = il comportamento di sempre, identico per bolla e anteprima.
   */
  fermo?: boolean;
  /**
   * ADR-0220 (la bolla dura quanto la clip): a ogni fotogramma del ciclo di composizione, il `currentTime` in secondi del colore e
   * dell'alfa. Chi la passa decide quando la clip e' finita o si e' fermata. Opzionale: assente = nessuna chiamata, nessun cambio.
   * Non gira mentre il lettore e' `fermo`.
   */
  onAvanzamento?: (colore: number, alfa: number) => void;
}

/** Tetto alla composizione (due `getImageData` per fotogramma): mai piu' di 30 al secondo, anche con un rAF a 60 o 120 Hz. */
const INTERVALLO_MIN_MS = 33;

/**
 * Phase 7c — dual-stream alpha compositor.
 *
 * Some browsers (notably iOS WebKit) don't decode WebM VP9 alpha. The
 * universal fallback is to ship the matte as TWO H.264 videos:
 *   1. RGB stream    — color, no alpha (every browser decodes H.264).
 *   2. Alpha stream  — grayscale; white pixel = opaque, black = transparent.
 *
 * We play them side-by-side (offscreen), then composite per frame on a
 * single canvas: read R,G,B from the RGB stream and use the alpha stream's
 * red channel as the per-pixel alpha. ~5 ms/frame on M-class mobile.
 *
 * Two videos must stay in sync — we drive the alpha video off the RGB
 * video's timeupdate so any drift gets corrected. Both are muted/looped/
 * autoplay/playsInline.
 */
export function DualStreamVideo({ rgbUrl, alphaUrl, poster, knownBgHex, className, onPronto, onErrore, liberaAllUscita, fermo = false, onAvanzamento }: Props) {
  const rgbRef = useRef<HTMLVideoElement | null>(null);
  const alphaRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  // Le opzioni di I-581 passano da un riferimento: l'effetto sotto riparte solo per `knownBgHex`, come prima.
  const opzioni = useRef({ onPronto, onErrore, liberaAllUscita, onAvanzamento });
  opzioni.current = { onPronto, onErrore, liberaAllUscita, onAvanzamento };
  // `fermo` non riavvia l'effetto (rileggerebbe i flussi): lo segue un secondo effetto, tramite queste due leve.
  const leve = useRef<{ ferma: () => void; riparti: () => void } | null>(null);
  const fermoIniziale = useRef(fermo);
  fermoIniziale.current = fermo;

  useEffect(() => {
    const rgb = rgbRef.current;
    const alpha = alphaRef.current;
    const canvas = canvasRef.current;
    if (!rgb || !alpha || !canvas) return;

    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return;

    let rafId = 0;
    let inPausa = fermoIniziale.current;
    let ultimoT = -1;
    let ultimaOra = -Infinity;
    let frameW = 0;
    let frameH = 0;
    let alphaScratch: HTMLCanvasElement | null = null;
    let alphaScratchCtx: CanvasRenderingContext2D | null = null;

    // Decompose backdrop tint on edge pixels when we know the bg color.
    const bg = knownBgHex ? hexToRgb(knownBgHex) : null;

    const onTimeUpdate = () => {
      // Keep alpha track in lock-step with RGB. Threshold to avoid endless
      // micro-adjustments.
      if (Math.abs(alpha.currentTime - rgb.currentTime) > 0.05) {
        alpha.currentTime = rgb.currentTime;
      }
    };
    rgb.addEventListener('timeupdate', onTimeUpdate);

    // I-581: pronto = il primo frame di tutti e due, una volta sola; errore = uno dei due non si carica.
    let segnalato = false;
    const controllaPronto = () => {
      if (segnalato || rgb.readyState < 2 || alpha.readyState < 2) return;
      segnalato = true;
      opzioni.current.onPronto?.();
    };
    const segnalaErrore = () => opzioni.current.onErrore?.();
    rgb.addEventListener('loadeddata', controllaPronto);
    alpha.addEventListener('loadeddata', controllaPronto);

    // 🔴 I due `<video>` sono `display:none` e Chromium NON fa partire l'autoplay di un elemento non visibile: restavano fermi a 0
    // (misurato su stg-tma, 11/10). Un `play()` ESPLICITO, flusso per flusso quando ha i dati, li fa partire. Un rifiuto non e' un errore:
    // resta il poster o il ripiego di chi ci ha montato (`onErrore` e' per i flussi che non si caricano). Nessun ciclo di tentativi.
    const avvia = (v: HTMLVideoElement) => {
      if (inPausa) return;
      try {
        void v.play()?.catch(() => {});
      } catch {
        /* play() assente o negato: resta fermo */
      }
    };
    const avviaRgb = () => avvia(rgb);
    const avviaAlpha = () => avvia(alpha);
    for (const [v, f] of [[rgb, avviaRgb], [alpha, avviaAlpha]] as const) {
      v.addEventListener('loadeddata', f);
      v.addEventListener('canplay', f);
      if (v.readyState >= 2) f();
    }
    rgb.addEventListener('error', segnalaErrore);
    alpha.addEventListener('error', segnalaErrore);
    controllaPronto();

    // Si ricompone SOLO con un fotogramma nuovo (`currentTime` del flusso rgb diverso dall'ultimo disegnato) e a non piu' di 30 Hz.
    // `requestVideoFrameCallback` non si usa: i due `<video>` sono `display:none` e non si sa se li chiami (da misurare).
    const fotogrammaNuovo = (t?: number) => {
      const ora = typeof t === 'number' ? t : performance.now();
      if (rgb.currentTime === ultimoT || ora - ultimaOra < INTERVALLO_MIN_MS) return false;
      ultimoT = rgb.currentTime;
      ultimaOra = ora;
      return true;
    };

    const draw = (t?: number) => {
      opzioni.current.onAvanzamento?.(rgb.currentTime, alpha.currentTime);
      if (rgb.readyState >= 2 && alpha.readyState >= 2 && !rgb.paused && !rgb.ended && fotogrammaNuovo(t)) {
        const W = rgb.videoWidth;
        const H = rgb.videoHeight;
        if (frameW !== W || frameH !== H) {
          frameW = W;
          frameH = H;
          canvas.width = W;
          canvas.height = H;
          alphaScratch = document.createElement('canvas');
          alphaScratch.width = W;
          alphaScratch.height = H;
          alphaScratchCtx = alphaScratch.getContext('2d', { willReadFrequently: true });
        }
        if (!alphaScratch || !alphaScratchCtx) return;

        // Draw RGB → main canvas.
        ctx.drawImage(rgb, 0, 0, frameW, frameH);
        const rgbData = ctx.getImageData(0, 0, frameW, frameH);

        // Draw alpha stream → scratch canvas, read pixel data.
        alphaScratchCtx.drawImage(alpha, 0, 0, frameW, frameH);
        const alphaData = alphaScratchCtx.getImageData(0, 0, frameW, frameH);

        // Combine: rgbData[a] = alphaData[r]. The alpha stream is grayscale
        // so R = G = B = perceived brightness; using R is fine and avoids
        // computing luminance.
        //
        // Decomposite on edges (when knownBgHex is set): for pixels with
        // partial alpha, the RGB stream is the alpha-blend of subject and
        // backdrop. Inverting the blend recovers the subject's true color
        // and removes the backdrop tint that otherwise shows up as a
        // colored halo around the silhouette:
        //   composite = subject·α + bg·(1−α)
        //   ⇒ subject = (composite − bg·(1−α)) / α
        const rgbPx = rgbData.data;
        const alphaPx = alphaData.data;
        if (bg) {
          for (let i = 0; i < rgbPx.length; i += 4) {
            const a = alphaPx[i];
            rgbPx[i + 3] = a;
            // Only decomposite on edge pixels — interior pixels (a ≈ 255)
            // are already the pure subject. Fully transparent pixels
            // (a = 0) get skipped because the formula divides by α.
            if (a > 4 && a < 250) {
              const an = a / 255;
              const inv = 1 - an;
              const r = (rgbPx[i] - bg.r * inv) / an;
              const g = (rgbPx[i + 1] - bg.g * inv) / an;
              const b = (rgbPx[i + 2] - bg.b * inv) / an;
              rgbPx[i]     = r < 0 ? 0 : r > 255 ? 255 : r;
              rgbPx[i + 1] = g < 0 ? 0 : g > 255 ? 255 : g;
              rgbPx[i + 2] = b < 0 ? 0 : b > 255 ? 255 : b;
            }
          }
        } else {
          for (let i = 0; i < rgbPx.length; i += 4) {
            rgbPx[i + 3] = alphaPx[i];
          }
        }
        ctx.putImageData(rgbData, 0, 0);
      }
      rafId = requestAnimationFrame(draw);
    };

    const ferma = () => {
      inPausa = true;
      cancelAnimationFrame(rafId);
      rafId = 0;
      rgb.pause();
      alpha.pause();
    };
    const riparti = () => {
      if (!inPausa) return;
      inPausa = false;
      ultimoT = -1;
      avvia(rgb);
      avvia(alpha);
      rafId = requestAnimationFrame(draw);
    };
    leve.current = { ferma, riparti };
    if (!inPausa) rafId = requestAnimationFrame(draw);
    return () => {
      leve.current = null;
      cancelAnimationFrame(rafId);
      rgb.removeEventListener('timeupdate', onTimeUpdate);
      rgb.removeEventListener('loadeddata', controllaPronto);
      alpha.removeEventListener('loadeddata', controllaPronto);
      for (const [v, f] of [[rgb, avviaRgb], [alpha, avviaAlpha]] as const) {
        v.removeEventListener('loadeddata', f);
        v.removeEventListener('canplay', f);
      }
      rgb.removeEventListener('error', segnalaErrore);
      alpha.removeEventListener('error', segnalaErrore);
      if (opzioni.current.liberaAllUscita) {
        for (const v of [rgb, alpha]) {
          v.pause();
          v.removeAttribute('src');
          v.load();
        }
      }
    };
  }, [knownBgHex]);

  useEffect(() => {
    if (fermo) leve.current?.ferma();
    else leve.current?.riparti();
  }, [fermo]);

  return (
    <>
      <video
        ref={rgbRef}
        src={rgbUrl}
        poster={poster}
        autoPlay
        loop
        muted
        playsInline
        crossOrigin="anonymous"
        className="hidden"
      />
      <video
        ref={alphaRef}
        src={alphaUrl}
        autoPlay
        loop
        muted
        playsInline
        crossOrigin="anonymous"
        className="hidden"
      />
      <canvas
        ref={canvasRef}
        className={cn('h-full w-full object-contain', className)}
      />
    </>
  );
}

function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const h = hex.replace('#', '');
  const v = h.length === 3 ? h.split('').map(c => c + c).join('') : h;
  const n = parseInt(v, 16);
  return { r: (n >> 16) & 0xff, g: (n >> 8) & 0xff, b: n & 0xff };
}
